// SAPNotes.jsx — Katalog / arsip / pelacakan SAP Note.
//
// Bukan downloader otomatis (butuh S-user interaktif → auth tetap di sesi SSO
// browser). Halaman ini: buka note di portal (tab baru), simpan metadata &
// status, dan arsipkan PDF (di-export manual dari portal) supaya tim bisa akses
// tanpa masing-masing punya S-user. Backend: sapNotesRoutes.js.
import { useState, useEffect, useCallback } from 'react';
import { api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import {
  FileText, ExternalLink, Save, Search, Upload, Eye, Download,
  Pencil, Trash2, X, AlertTriangle, Loader2, Plus, Paperclip, DownloadCloud, Server,
  Info, Check, User, Layers, Clock, ClipboardList,
} from 'lucide-react';

const PORTAL_URL = (num) => `https://me.sap.com/notes/${encodeURIComponent(num)}`;

const STATUS = {
  review:   { key: 'sapnote.statusReview',   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  applying: { key: 'sapnote.statusApplying', cls: 'bg-brand-50 text-brand-700 border-brand-200' },
  done:     { key: 'sapnote.statusDone',     cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const fmtBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const fmtTs = (ts) => { try { return new Date(ts.includes('Z') || ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z').toLocaleString('id-ID'); } catch { return ts || '—'; } };

// Urutan tahap status (dipakai stepper detail note).
const STATUS_STEPS = ['review', 'applying', 'done'];

// Info terstruktur yang “terkubur” di remark saat impor dari sistem / auto-isi PDF
// (mis. "Dari sistem D01 · v3 · Implemented · oleh ABAP01"). Best-effort & toleran:
// kalau tak ketemu, field dikosongkan (panel menyembunyikannya).
function parseRemarkMeta(remark) {
  const t = String(remark || '');
  const m = (re) => { const x = t.match(re); return x ? x[1].trim() : ''; };
  return {
    version:     m(/(?:\bv|Versi:\s*)(\d+)\b/i),
    fromSystem:  m(/Dari sistem\s+([^·\n]+?)(?:\s*·|$)/i),
    processedBy: m(/\boleh\s+([^·\n]+?)(?:\s*·|$)/i),
  };
}

function StatusBadge({ status }) {
  const { t } = useLang();
  const s = STATUS[status] || STATUS.review;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${s.cls}`}>{t(s.key)}</span>;
}

// Cuplikan hasil pencarian di ISI note, dgn kata kunci ditebalkan. `text` sudah
// plaintext dari server (aman dirender apa adanya via React).
function ContentSnippet({ text, query }) {
  const q = String(query || '').trim();
  let parts = [text];
  if (q) {
    const rx = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
    parts = String(text).split(rx);
  }
  return (
    <div className="mt-0.5 flex items-start gap-1 text-[11px] text-slate-400 max-w-[280px]">
      <Search style={{ width: 11, height: 11 }} className="mt-[3px] shrink-0" />
      <span className="truncate italic" title={text}>
        {parts.map((p, i) => (q && p.toLowerCase() === q.toLowerCase())
          ? <mark key={i} className="bg-amber-100 text-amber-800 rounded px-0.5 not-italic">{p}</mark>
          : <span key={i}>{p}</span>)}
      </span>
    </div>
  );
}

export default function SAPNotes() {
  const { t } = useLang();
  const [notes, setNotes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState('');
  const [toast, setToast]       = useState(null); // { type, msg }
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [newNum, setNewNum]     = useState('');
  const [editing, setEditing]   = useState(null); // note object being edited/created in modal
  const [busy, setBusy]         = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [mainSort, setMainSort]     = useState({ key: 'note_number', dir: 'asc' });
  const [selected, setSelected]     = useState(() => new Set()); // id note terpilih (multiselect)
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [pdfViewer, setPdfViewer]   = useState(null); // { note_number, name, url } — preview satu file
  const [filesModal, setFilesModal] = useState(null); // { note, files:[]|null } — manajer lampiran
  const [detail, setDetail]         = useState(null); // { note, files:[]|null } — panel detail gaya SNOTE
  const [sysContent, setSysContent] = useState(null); // null | { loading?, html, has_text, needs_download, version, language, error?, notConfigured? }
  const [noteView, setNoteView]     = useState(null); // null | { note, status:'loading'|'working'|'html'|'pdf'|'empty', name?, html?, url? }
  const [parsing, setParsing]       = useState(false);
  const [pendingPdf, setPendingPdf] = useState(null); // File dari "Isi dari PDF" → dilampirkan otomatis saat note dibuat
  const [sysModal, setSysModal]     = useState(null); // { system, notes, loading, sel:Set } — tarik dari sistem
  const [importing, setImporting]   = useState(false);
  const [autoArchive, setAutoArchive] = useState(true);   // saat impor: arsipkan isi note yg layak
  const [archiveJob, setArchiveJob]   = useState(null);   // { done, total } — progres arsip otomatis
  const [sysSearch, setSysSearch]   = useState('');
  const [sysStatusF, setSysStatusF] = useState('');
  const [sysSort, setSysSort]       = useState({ key: 'note_number', dir: 'asc' });

  const [bulkOpen, setBulkOpen]     = useState(false);     // modal tambah banyak nomor
  const [bulkText, setBulkText]     = useState('');
  const [bulkPull, setBulkPull]     = useState(true);      // setelah tambah: tarik & arsipkan isi dari sistem
  const [bulkResult, setBulkResult] = useState(null);      // { created, skipped, results[] }
  const [sysDownloading, setSysDownloading] = useState(false);
  const [extModal, setExtModal] = useState(null);           // { token, copied, error }
  const openExtModal = async () => {
    setExtModal({ token: '' });
    try {
      const { data } = await axiosApi.get('/api/sap-notes/capture-token');
      let queue = null; try { queue = (await axiosApi.get('/api/sap-notes/capture-queue/count')).data.count; } catch { /* abaikan */ }
      setExtModal({ token: data.token, queue });
    }
    catch (e) { setExtModal({ token: '', error: e.response?.data?.error || e.message }); }
  };
  const rotateExtToken = async () => {
    try { const { data } = await axiosApi.post('/api/sap-notes/capture-token/rotate'); setExtModal({ token: data.token }); flash('success', 'New token created — update it in the extension settings.'); }
    catch (e) { flash('error', e.response?.data?.error || e.message); }
  };

  const flash = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 3200); };

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const { data } = await axiosApi.get('/api/sap-notes', { params });
      setNotes(data.notes || []);
      setSelected(new Set());   // reset pilihan tiap daftar dimuat ulang
    } catch (e) { setErr(e.response?.data?.error || e.message); setNotes([]); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openInPortal = (num) => window.open(PORTAL_URL(num), '_blank', 'noopener');

  // Simpan cepat dari kotak atas (buka modal metadata dgn nomor terisi).
  const quickAdd = () => {
    const num = newNum.trim();
    if (!/^\d{1,12}$/.test(num)) { flash('error', t('sapnote.errNoteNumber')); return; }
    setPendingPdf(null);
    setEditing({ note_number: num, title: '', component: '', system: '', priority: '', status: 'review', remark: '' });
  };

  // Auto-isi dari PDF: unggah PDF isi note → server ekstrak header → buka form
  // terisi otomatis. PDF-nya disimpan sementara (pendingPdf) & dilampirkan ke
  // note begitu disimpan, jadi satu aksi: pilih PDF → cek → Simpan.
  const parseFromPdf = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axiosApi.post('/api/sap-notes/parse-pdf', fd);
      const p = data.parsed || {};
      setPendingPdf(file);
      setEditing({
        note_number: p.note_number || newNum.trim() || '',
        title: p.title || '', component: p.component || '', system: '',
        priority: p.priority || '', status: 'review', remark: p.remark || '',
      });
      flash('success', t('sapnote.pdfFilled'));
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      setPendingPdf(null);
      flash('error', msg);
    } finally { setParsing(false); }
  };

  const saveNote = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      if (editing.id) {
        const { data } = await axiosApi.put(`/api/sap-notes/${editing.id}`, editing);
        setNotes((prev) => prev.map((n) => (n.id === data.note.id ? data.note : n)));
        flash('success', t('sapnote.noteUpdated', { n: data.note.note_number }));
      } else {
        const { data } = await axiosApi.post('/api/sap-notes', editing);
        let created = data.note;
        // Lampirkan PDF yang dipakai untuk auto-isi (kalau ada) ke note baru.
        if (pendingPdf) {
          try {
            const fd = new FormData(); fd.append('files', pendingPdf);
            const up = await axiosApi.post(`/api/sap-notes/${created.id}/files`, fd);
            created = up.data.note;
          } catch (e) { flash('error', t('sapnote.saveButAttachFail') + (e.response?.data?.error || e.message)); }
        }
        setNotes((prev) => [created, ...prev]);
        flash('success', t(pendingPdf ? 'sapnote.noteSavedPdf' : 'sapnote.noteSaved', { n: created.note_number }));
        setNewNum('');
      }
      setPendingPdf(null);
      setEditing(null);
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await axiosApi.delete(`/api/sap-notes/${confirmDel.id}`);
      setNotes((prev) => prev.filter((n) => n.id !== confirmDel.id));
      setSelected((s) => { const x = new Set(s); x.delete(confirmDel.id); return x; });
      flash('success', t('sapnote.noteDeleted', { n: confirmDel.note_number }));
      setConfirmDel(null);
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await axiosApi.post('/api/sap-notes/bulk-delete', { ids });
      setNotes((prev) => prev.filter((n) => !selected.has(n.id)));
      setSelected(new Set());
      setBulkConfirm(false);
      flash('success', t('sapnote.notesDeleted', { count: data.deleted }));
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  // Multiselect helpers
  const toggleRow = (id) => setSelected((s) => { const x = new Set(s); x.has(id) ? x.delete(id) : x.add(id); return x; });
  const mainToggleSort = (key) => setMainSort((p) => ({ key, dir: p.key === key && p.dir === 'asc' ? 'desc' : 'asc' }));
  const mainArrow = (key) => (mainSort.key === key ? (mainSort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  // ── Lampiran (banyak file per note) ──────────────────────────────────────────
  const openFiles = async (note) => {
    setFilesModal({ note, files: null });
    try {
      const { data } = await axiosApi.get(`/api/sap-notes/${note.id}/files`);
      setFilesModal({ note, files: data.files || [] });
    } catch (e) { flash('error', e.response?.data?.error || e.message); setFilesModal({ note, files: [] }); }
  };
  const uploadFiles = async (note, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData(); files.forEach((f) => fd.append('files', f));
      const { data } = await axiosApi.post(`/api/sap-notes/${note.id}/files`, fd);
      setFilesModal((m) => (m && m.note.id === note.id ? { ...m, files: data.files } : m));
      setNotes((prev) => prev.map((n) => (n.id === data.note.id ? data.note : n)));
      flash('success', t('sapnote.attachAdded', { count: files.length }));
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };
  const downloadFile = async (note, f) => {
    try {
      const res = await axiosApi.get(`/api/sap-notes/${note.id}/files/${f.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = f.name || `lampiran-${f.id}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
  };
  const viewFile = async (note, f) => {
    try {
      const res = await axiosApi.get(`/api/sap-notes/${note.id}/files/${f.id}`, { responseType: 'blob' });
      setPdfViewer({ note_number: note.note_number, name: f.name, url: URL.createObjectURL(res.data) });
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
  };
  const deleteFile = async (note, f) => {
    setBusy(true);
    try {
      const { data } = await axiosApi.delete(`/api/sap-notes/${note.id}/files/${f.id}`);
      setFilesModal((m) => (m && m.note.id === note.id ? { ...m, files: data.files } : m));
      setNotes((prev) => prev.map((n) => (n.id === data.note.id ? data.note : n)));
      flash('success', t('sapnote.attachDeleted'));
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };
  const isPreviewable = (name) => /\.(pdf|png|jpe?g|gif|webp|svg|txt|log|md|json|xml|csv)$/i.test(name || '');

  // ── Detail note (Properties + Status, gaya SNOTE) ────────────────────────────
  // Murni dari data register yang sudah ada + lampiran (fetch endpoint existing).
  // Tidak menyentuh DB / tidak ada endpoint baru.
  const openDetail = async (note) => {
    setSysContent(null);   // konten sistem di-load lazy (tak auto-hit SAP)
    setDetail({ note, files: null });
    try {
      const { data } = await axiosApi.get(`/api/sap-notes/${note.id}/files`);
      const files = data.files || [];
      setDetail((d) => (d && d.note.id === note.id ? { ...d, files } : d));
      // Kalau SUDAH ada arsip HTML → tampilkan langsung (konsisten dgn ikon mata),
      // tanpa hit SAP. Ambil arsip terbaru.
      const htmls = files.filter((f) => /\.html?$/i.test(f.name));
      if (htmls.length) {
        const hf = htmls[htmls.length - 1];
        const vm = hf.name.match(/_v(\d+)/i);
        try {
          const res = await axiosApi.get(`/api/sap-notes/${note.id}/files/${hf.id}`, { responseType: 'text' });
          setSysContent((c) => (c ? c : { has_text: true, html: String(res.data || ''), fromArchive: true, version: vm ? vm[1] : '' }));
        } catch { /* biarkan tombol muncul */ }
      }
    } catch { setDetail((d) => (d && d.note.id === note.id ? { ...d, files: [] } : d)); }
  };
  // Muat isi long text dari sistem SAP + ARSIPKAN (permanen) sekali jalan, lalu
  // tampilkan. Dipakai semua tombol "Muat"/"Muat ulang" di panel Detail agar isi
  // note konsisten dgn ikon mata (satu sumber: arsip). Dedupe per nama file.
  const loadAndArchiveDetail = async (note) => {
    setSysContent({ loading: true });
    try {
      const { data } = await axiosApi.get('/api/sap-notes/system/detail', { params: { note: note.note_number } });
      if (!data.has_text) { setSysContent({ ...data, loading: false }); return; }
      const fname = htmlFileName(note, data.version);
      const existing = (detail?.files || []).some((f) => f.name === fname);
      if (!existing) {
        try {
          const file = new File([data.html], fname, { type: 'text/html' });
          const fd = new FormData(); fd.append('files', file);
          const up = await axiosApi.post(`/api/sap-notes/${note.id}/files`, fd);
          setNotes((prev) => prev.map((n) => (n.id === up.data.note.id ? up.data.note : n)));
          setDetail((d) => (d && d.note.id === note.id ? { ...d, note: up.data.note, files: up.data.files } : d));
        } catch { /* kalau gagal arsip, tetap tampilkan isinya */ }
      }
      setSysContent({ has_text: true, html: data.html, version: data.version, language: data.language, fromArchive: true });
    } catch (e) {
      setSysContent({ loading: false, error: e.response?.data?.error || e.message, notConfigured: e.response?.status === 501 });
    }
  };

  // #2 — nama file arsip HTML (konsisten dgn loadAndArchiveDetail & auto-arsip impor).
  const htmlFileName = (note, version) => `SAP_Note_${note.note_number}${version ? `_v${version}` : ''}.html`;
  const downloadSystemHtml = (note, html, version) => {
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const a = document.createElement('a'); a.href = url; a.download = htmlFileName(note, version);
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ── Viewer note SERAGAM (ikon mata di kolom Aksi) ────────────────────────────
  // Satu pintu melihat isi note: arsip HTML (dari sistem) ATAU PDF (dari upload).
  // Ambil lampiran terbaru bertipe html dulu, lalu pdf. Kalau belum ada, tawarkan
  // tarik+arsip dari sistem sekali klik (jadi permanen).
  const pickNoteContentFile = (files) => {
    const list = files || [];
    const htmls = list.filter((f) => /\.html?$/i.test(f.name));
    if (htmls.length) return { file: htmls[htmls.length - 1], kind: 'html' };
    const pdfs = list.filter((f) => /\.pdf$/i.test(f.name));
    if (pdfs.length) return { file: pdfs[pdfs.length - 1], kind: 'pdf' };
    return null;
  };
  const closeNoteView = () => setNoteView((v) => { if (v?.url) URL.revokeObjectURL(v.url); return null; });
  const openNoteView = async (note) => {
    setNoteView({ note, status: 'loading' });
    try {
      const { data } = await axiosApi.get(`/api/sap-notes/${note.id}/files`);
      const picked = pickNoteContentFile(data.files || []);
      if (!picked) { setNoteView({ note, status: note.note_type === 'KBA' ? 'kba' : 'empty' }); return; }
      if (picked.kind === 'html') {
        const res = await axiosApi.get(`/api/sap-notes/${note.id}/files/${picked.file.id}`, { responseType: 'text' });
        setNoteView({ note, status: 'html', name: picked.file.name, html: String(res.data || '') });
      } else {
        const res = await axiosApi.get(`/api/sap-notes/${note.id}/files/${picked.file.id}`, { responseType: 'blob' });
        setNoteView({ note, status: 'pdf', name: picked.file.name, url: URL.createObjectURL(res.data) });
      }
    } catch (e) { flash('error', e.response?.data?.error || e.message); setNoteView(null); }
  };
  // Dari state 'empty': tarik isi dari sistem → arsipkan sbg lampiran → tampilkan.
  const fetchAndArchiveNote = async (note) => {
    setNoteView((v) => (v ? { ...v, status: 'working' } : v));
    try {
      let { data } = await axiosApi.get('/api/sap-notes/system/detail', { params: { note: note.note_number } });
      // Belum ada di SNOTE → minta sistem download dari SAP Support Portal dulu, lalu coba lagi.
      if (!data.has_text && data.needs_download) {
        flash('success', `Note ${note.note_number} not in system yet — downloading via SNOTE…`);
        const res = await axiosApi.post('/api/sap-notes/system/download', { notes: [note.note_number] });
        const r0 = (res.data.results || [])[0];
        if (r0?.ok) ({ data } = await axiosApi.get('/api/sap-notes/system/detail', { params: { note: note.note_number } }));
        else if (r0?.kba) {
          markKbaLocal([note.note_number]);
          flash('error', `${note.note_number} is a KBA — SNOTE cannot download it.`);
          setNoteView((v) => (v ? { ...v, note: { ...v.note, note_type: 'KBA' }, status: 'kba' } : v));
          return;
        }
        else { flash('error', r0?.error || 'Download to system failed.'); setNoteView((v) => (v ? { ...v, status: 'empty' } : v)); return; }
      }
      if (!data.has_text) {
        flash('error', data.needs_download ? t('sapnote.notDownloadedFlash') : t('sapnote.contentUnavailable'));
        setNoteView((v) => (v ? { ...v, status: 'empty' } : v));
        return;
      }
      const fname = htmlFileName(note, data.version);
      const file = new File([data.html], fname, { type: 'text/html' });
      const fd = new FormData(); fd.append('files', file);
      const up = await axiosApi.post(`/api/sap-notes/${note.id}/files`, fd);
      setNotes((prev) => prev.map((n) => (n.id === up.data.note.id ? up.data.note : n)));
      setDetail((d) => (d && d.note.id === note.id ? { ...d, note: up.data.note, files: up.data.files } : d));
      setNoteView({ note, status: 'html', name: fname, html: data.html });
      flash('success', t('sapnote.contentPulled'));
    } catch (e) {
      flash('error', e.response?.status === 501 ? t('sapnote.pullNotConfiguredShort') : (e.response?.data?.error || e.message));
      setNoteView((v) => (v ? { ...v, status: 'empty' } : v));
    }
  };
  // Ganti status cepat dari panel detail — pakai PUT existing (partial update).
  const quickStatus = async (note, status) => {
    if (note.status === status || busy) return;
    setBusy(true);
    try {
      const { data } = await axiosApi.put(`/api/sap-notes/${note.id}`, { status });
      setNotes((prev) => prev.map((n) => (n.id === data.note.id ? data.note : n)));
      setDetail((d) => (d && d.note.id === note.id ? { ...d, note: data.note } : d));
      flash('success', `Status → ${STATUS[status] ? t(STATUS[status].key) : status}.`);
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  // ── Tarik dari sistem SAP ────────────────────────────────────────────────────
  const fetchFromSystem = async () => {
    setSysSearch(''); setSysStatusF(''); setSysSort({ key: 'note_number', dir: 'asc' });
    setSysModal({ system: '', notes: [], loading: true, sel: new Set() });
    try {
      const { data } = await axiosApi.get('/api/sap-notes/system', { params: { max: 2000 } });
      // Pra-pilih hanya yang BELUM ada di register (biar cepat impor yang baru).
      // Pra-pilih hanya yang BELUM ada di register DAN punya judul/komponen (yang
      // kosong biasanya belum ada di sistem → dilewati biar tak jadi baris kosong).
      const sel = new Set((data.notes || [])
        .filter((n) => !n.in_register && (String(n.title || '').trim() || String(n.component || '').trim()))
        .map((n) => n.note_number));
      setSysModal({ system: data.system || '', notes: data.notes || [], loading: false, sel });
    } catch (e) {
      setSysModal(null);
      flash('error', e.response?.data?.error || e.message);
    }
  };
  const toggleSel = (num) => setSysModal((m) => {
    const sel = new Set(m.sel); sel.has(num) ? sel.delete(num) : sel.add(num); return { ...m, sel };
  });
  // Kelayakan auto-arsip saat impor: SKIP hanya jika judul & komponen dua-duanya
  // kosong (biasanya note belum benar-benar ada di sistem → isinya tak tersedia).
  // Status TIDAK menjadi penyaring — note 'Perlu ditinjau' pun diarsipkan bila ada metadata.
  const eligibleForAutoArchive = (row) =>
    String(row.title || '').trim() !== '' || String(row.component || '').trim() !== '';
  // Setelah impor: untuk note terpilih yg layak & belum punya arsip HTML, tarik isi
  // dari sistem lalu arsipkan sbg lampiran. Gagal per-note tidak menghentikan sisanya.
  const archiveImportedContents = async (chosen) => {
    const eligible = chosen.filter(eligibleForAutoArchive);
    if (!eligible.length) { load(); return; }
    let idByNum = {};
    try {
      const { data } = await axiosApi.get('/api/sap-notes');
      for (const n of (data.notes || [])) idByNum[String(n.note_number)] = n.id;
    } catch { load(); return; }
    setArchiveJob({ done: 0, total: eligible.length });
    let okc = 0;
    for (const row of eligible) {
      const id = idByNum[String(row.note_number)];
      if (id) {
        try {
          const f = await axiosApi.get(`/api/sap-notes/${id}/files`);
          const already = (f.data.files || []).some((x) => /\.html?$/i.test(x.name));
          if (!already) {
            const { data } = await axiosApi.get('/api/sap-notes/system/detail', { params: { note: row.note_number } });
            if (data.has_text) {
              const fname = `SAP_Note_${row.note_number}${data.version ? `_v${data.version}` : ''}.html`;
              const file = new File([data.html], fname, { type: 'text/html' });
              const fd = new FormData(); fd.append('files', file);
              await axiosApi.post(`/api/sap-notes/${id}/files`, fd);
              okc++;
            }
          }
        } catch { /* lewati kegagalan per-note */ }
      }
      setArchiveJob((j) => ({ done: (j?.done || 0) + 1, total: eligible.length }));
    }
    setArchiveJob(null);
    flash('success', t('sapnote.contentArchivedAuto', { count: okc }));
    load();
  };

  const importSelected = async () => {
    if (!sysModal) return;
    const chosen = sysModal.notes.filter((n) => sysModal.sel.has(n.note_number));
    if (!chosen.length) { flash('error', t('sapnote.selectAtLeastOne')); return; }
    setImporting(true);
    try {
      const { data } = await axiosApi.post('/api/sap-notes/system/import', { system: sysModal.system, notes: chosen });
      flash('success', t('sapnote.importResult', { created: data.created, skipped: data.skipped }) + (data.invalid ? t('sapnote.importInvalid', { invalid: data.invalid }) : '') + '.');
      const doArchive = autoArchive;
      setSysModal(null);
      if (doArchive) await archiveImportedContents(chosen);
      else load();
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
    finally { setImporting(false); }
  };

  // ── Tambah banyak nomor sekaligus ────────────────────────────────────────────
  const bulkNums = [...new Set((bulkText.match(/\d{1,12}/g) || []).map((n) => n.replace(/^0+/, '') || n))];
  const runBulkAdd = async () => {
    if (!bulkNums.length) { flash('error', 'Enter at least one note number.'); return; }
    setBusy(true); setBulkResult(null);
    try {
      const { data } = await axiosApi.post('/api/sap-notes/bulk-add', { numbers: bulkNums });
      setBulkResult(data);
      flash('success', `${data.created} note(s) added, ${data.skipped} already in list.`);
      const inSys = (data.results || []).filter((r) => r.result === 'created' && r.in_system);
      if (bulkPull && inSys.length) await archiveImportedContents(inSys.map((r) => ({ note_number: r.note_number, title: 'x' })));
      else load();
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };
  const closeBulk = () => { if (busy) return; setBulkOpen(false); setBulkText(''); setBulkResult(null); };

  // ── Minta sistem SAP download note dari support portal (SNOTE via Z-service) ──
  const markKbaLocal = (nums) => {
    const set = new Set(nums.map(String));
    setNotes((prev) => prev.map((x) => (set.has(String(x.note_number)) ? { ...x, note_type: 'KBA' } : x)));
    setDetail((d) => (d && set.has(String(d.note.note_number)) ? { ...d, note: { ...d.note, note_type: 'KBA' } } : d));
  };
  const downloadToSystem = async (nums, note) => {
    setSysDownloading(true);
    try {
      const { data } = await axiosApi.post('/api/sap-notes/system/download', { notes: nums });
      const res = data.results || [];
      const ok = res.filter((r) => r.ok).length;
      const kbas = res.filter((r) => r.kba).map((r) => r.note_number);
      if (kbas.length) markKbaLocal(kbas);
      if (ok) flash('success', `${ok}/${res.length} note(s) downloaded to the SAP system.` + (kbas.length ? ` ${kbas.length} KBA(s) detected — capture from SAP for Me.` : ''));
      else if (kbas.length) flash('error', kbas.length === 1 ? `${kbas[0]} is a KBA — SNOTE cannot download it. Open it in SAP for Me and save the PDF.` : `${kbas.length} KBA(s) detected — SNOTE cannot download KBAs.`);
      else flash('error', res[0]?.error || 'Download failed.');
      if (note && kbas.includes(String(note.note_number))) setSysContent({ kba: true });
      if (ok && note) await loadAndArchiveDetail(note);
      return res;
    } catch (e) { flash('error', e.response?.data?.error || e.message); return []; }
    finally { setSysDownloading(false); }
  };

  const iconBtn = 'p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition disabled:opacity-40';

  // Daftar note-sistem setelah filter (cari + status) & sort — dihitung saat render.
  const sysStatusOpts = sysModal && !sysModal.loading
    ? [...new Set(sysModal.notes.map((n) => n.impl_label || '—'))].sort()
    : [];
  const sysVisible = (() => {
    if (!sysModal || sysModal.loading) return [];
    let arr = sysModal.notes;
    const q = sysSearch.trim().toLowerCase();
    if (q) arr = arr.filter((n) => `${n.note_number} ${n.component} ${n.impl_label} ${n.version}`.toLowerCase().includes(q));
    if (sysStatusF) arr = arr.filter((n) => (n.impl_label || '—') === sysStatusF);
    const { key, dir } = sysSort; const s = dir === 'asc' ? 1 : -1;
    return [...arr].sort((a, b) => {
      if (key === 'note_number' || key === 'version')
        return s * ((Number(a[key]) || 0) - (Number(b[key]) || 0));
      return s * String(a[key] || '').localeCompare(String(b[key] || ''));
    });
  })();
  const sysToggleSort = (key) => setSysSort((p) => ({ key, dir: p.key === key && p.dir === 'asc' ? 'desc' : 'asc' }));
  const sysSortArrow = (key) => (sysSort.key === key ? (sysSort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  // Tabel utama: urutkan client-side sesuai kolom yang diklik.
  const STATUS_ORDER = { review: 0, applying: 1, done: 2 };
  const sortedNotes = (() => {
    const { key, dir } = mainSort; const s = dir === 'asc' ? 1 : -1;
    return [...notes].sort((a, b) => {
      if (key === 'note_number') return s * ((Number(a.note_number) || 0) - (Number(b.note_number) || 0));
      if (key === 'status')      return s * ((STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));
      if (key === 'file_count')  return s * ((a.file_count || 0) - (b.file_count || 0));
      return s * String(a[key] || '').localeCompare(String(b[key] || ''));
    });
  })();
  const allSelected = sortedNotes.length > 0 && sortedNotes.every((n) => selected.has(n.id));
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(sortedNotes.map((n) => n.id)));

  // Note "kosong" = judul DAN komponen dua-duanya kosong (biasanya belum benar-benar
  // ada di sistem). Tombol bersih-bersih memilih semua lalu minta konfirmasi hapus.
  const emptyIds = notes.filter((n) => !String(n.title || '').trim() && !String(n.component || '').trim()).map((n) => n.id);
  const cleanupEmpty = () => {
    if (!emptyIds.length) { flash('error', t('sapnote.noEmptyNotes')); return; }
    setSelected(new Set(emptyIds));
    setBulkConfirm(true);
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="mb-4" style={{ background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>{t('sapnote.sapNote')}</h1>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>{t('sapnote.subtitle')}</div>
      </div>

      {/* Tambah / buka note */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 mb-4">
        <div className="text-[13px] font-semibold text-slate-700 mb-2.5 flex items-center gap-1.5"><Plus style={{ width: 15, height: 15 }} />{t('sapnote.addOpenNote')}</div>
        <div className="flex gap-2.5 items-end flex-wrap">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[12px] text-slate-500 mb-1">{t('sapnote.noteNumber')}</label>
            <input value={newNum} onChange={(e) => setNewNum(e.target.value.replace(/[^\d]/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && quickAdd()}
              placeholder={t('sapnote.egNoteNumber')} inputMode="numeric"
              className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
          </div>
          <button onClick={() => newNum.trim() ? openInPortal(newNum.trim()) : flash('error', t('sapnote.fillNumberFirst'))}
            className="h-9 px-3.5 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-1.5">
            <ExternalLink style={{ width: 15, height: 15 }} />{t('sapnote.openPortal')}</button>
          <label className={`h-9 px-3.5 rounded-lg border text-sm flex items-center gap-1.5 cursor-pointer ${parsing ? 'border-slate-200 text-slate-400' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
            title={t('sapnote.uploadPdfTitle')}>
            {parsing ? <Loader2 className="animate-spin" style={{ width: 15, height: 15 }} /> : <Upload style={{ width: 15, height: 15 }} />} {t('sapnote.fillFromPdf')}
            <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={parsing}
              onChange={(e) => { parseFromPdf(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          <button onClick={quickAdd}
            className="h-9 px-3.5 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700 flex items-center gap-1.5">
            <Save style={{ width: 15, height: 15 }} />{t('sapnote.saveToList')}</button>
          <button onClick={() => { setBulkResult(null); setBulkOpen(true); }}
            className="h-9 px-3.5 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
            title="Paste many note numbers at once">
            <ClipboardList style={{ width: 15, height: 15 }} />Add multiple</button>
          <button onClick={openExtModal}
            className="h-9 px-3.5 rounded-lg border border-violet-300 text-sm text-violet-700 bg-violet-50 hover:bg-violet-100 flex items-center gap-1.5"
            title="Chrome extension to send SAP Notes / KBAs from SAP for Me to this page">
            <DownloadCloud style={{ width: 15, height: 15 }} />Capture extension</button>
          <button onClick={fetchFromSystem}
            className="h-9 px-3.5 rounded-lg border border-brand-300 text-sm text-brand-700 bg-brand-50 hover:bg-brand-100 flex items-center gap-1.5"
            title={t('sapnote.fetchListTitle')}>
            <DownloadCloud style={{ width: 15, height: 15 }} />{t('sapnote.pullFromSystem')}</button>
        </div>
        <p className="text-[12.5px] leading-relaxed text-slate-700 mt-2 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle style={{ width: 14, height: 14 }} className="mt-0.5 shrink-0 text-amber-600" />
          {t('sapnote.portalBanner')}
        </p>
      </div>

      {/* Filter */}
      <div className="flex gap-2 items-center mb-2.5 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search style={{ width: 15, height: 15 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('sapnote.searchPlaceholder')}
            className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 px-2.5 rounded-lg border border-slate-300 text-sm bg-white outline-none">
          <option value="">{t('sapnote.allStatus')}</option>
          <option value="review">{t('sapnote.statusReview')}</option>
          <option value="applying">{t('sapnote.statusApplying')}</option>
          <option value="done">{t('sapnote.statusDone')}</option>
        </select>
        {emptyIds.length > 0 && (
          <button onClick={cleanupEmpty}
            className="h-9 px-3 rounded-lg border border-rose-200 text-sm text-rose-600 bg-rose-50 hover:bg-rose-100 inline-flex items-center gap-1.5"
            title={t('sapnote.selectDeleteEmpty')}>
            <Trash2 style={{ width: 15, height: 15 }} /> Clean up empty ({emptyIds.length})
          </button>
        )}
      </div>

      {err && <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-[12.5px] text-rose-700">{err}</div>}

      {/* Bar aksi massal — muncul saat ada baris terpilih */}
      {selected.size > 0 && (
        <div className="mb-2 flex items-center gap-3 px-3 py-2 bg-brand-50 border border-brand-200 rounded-lg text-[13px]">
          <span className="text-brand-700 font-medium">{selected.size} note(s) selected</span>
          <button onClick={() => setSelected(new Set())} className="text-slate-500 hover:underline">{t('sapnote.cancelSelect')}</button>
          <button onClick={() => setBulkConfirm(true)} disabled={busy}
            className="ml-auto h-8 px-3 rounded-lg bg-rose-600 text-white text-[13px] hover:bg-rose-700 flex items-center gap-1.5 disabled:opacity-50">
            <Trash2 style={{ width: 14, height: 14 }} /> Delete selected ({selected.size})
          </button>
        </div>
      )}

      {/* Tabel */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                <th className="px-3 py-2.5 w-8 border-b border-slate-200 text-left">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} title={t('sapnote.selectAllShown')} />
                </th>
                {[['note_number', t('sapnote.colNumber')], ['title', t('sapnote.title')], ['component', t('sapnote.component')], ['system', t('sapnote.system')], ['status', t('sapnote.status')], ['file_count', t('sapnote.attachments')]].map(([k, label]) => (
                  <th key={k} onClick={() => mainToggleSort(k)}
                    className="px-3 py-2.5 font-medium text-[11px] uppercase tracking-wide border-b border-slate-200 text-left cursor-pointer select-none hover:text-slate-700">
                    {label}{mainArrow(k)}
                  </th>
                ))}
                <th className="px-3 py-2.5 font-medium text-[11px] uppercase tracking-wide border-b border-slate-200 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" style={{ width: 16, height: 16 }} />{t('sapnote.loading')}</td></tr>
              ) : sortedNotes.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400">{t('sapnote.noNotes')}</td></tr>
              ) : sortedNotes.map((n) => (
                <tr key={n.id} className={`hover:bg-slate-50/60 ${selected.has(n.id) ? 'bg-brand-50/50' : ''}`}>
                  <td className="px-3 py-2.5 border-b border-slate-100">
                    <input type="checkbox" checked={selected.has(n.id)} onChange={() => toggleRow(n.id)} />
                  </td>
                  <td className="px-3 py-2.5 border-b border-slate-100">
                    <button onClick={() => openDetail(n)} className="text-brand-600 hover:underline font-medium" title={t('sapnote.noteDetail')}>{n.note_number}</button>
                    {n.note_type === 'KBA' && <span className="ml-1.5 px-1.5 py-px rounded text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200 align-middle" title="Knowledge Base Article — not downloadable via SNOTE">KBA</span>}
                  </td>
                  <td className="px-3 py-2.5 border-b border-slate-100 text-slate-800 max-w-[280px]" title={n.title || 'View note content'}>
                    <button onClick={() => openNoteView(n)} className="block text-left hover:text-brand-600 hover:underline truncate max-w-full align-middle">
                      {n.title || <span className="text-slate-400">—</span>}
                    </button>
                    {n.content_match && n.content_snippet && <ContentSnippet text={n.content_snippet} query={search.trim()} />}
                  </td>
                  <td className="px-3 py-2.5 border-b border-slate-100 text-slate-600">{n.component || '—'}</td>
                  <td className="px-3 py-2.5 border-b border-slate-100 text-slate-600">{n.system || '—'}</td>
                  <td className="px-3 py-2.5 border-b border-slate-100"><StatusBadge status={n.status} /></td>
                  <td className="px-3 py-2.5 border-b border-slate-100">
                    <button onClick={() => openFiles(n)}
                      className={`inline-flex items-center gap-1 text-[11px] ${n.file_count ? 'text-emerald-600 hover:text-emerald-700' : 'text-slate-400 hover:text-slate-600'}`}
                      title={t('sapnote.manageAttachments')}>
                      <Paperclip style={{ width: 12, height: 12 }} />
                      {n.file_count ? `${n.file_count} file` : 'manage'}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 border-b border-slate-100">
                    <div className="flex items-center justify-end gap-0.5">
                      <button onClick={() => openDetail(n)} className={iconBtn} title={t('sapnote.noteDetail')}><Info style={{ width: 15, height: 15 }} /></button>
                      <button onClick={() => openNoteView(n)} className={iconBtn} title={t('sapnote.viewNoteContent')}><Eye style={{ width: 15, height: 15 }} /></button>
                      <button onClick={() => openInPortal(n.note_number)} className={iconBtn} title={t('sapnote.openPortal')}><ExternalLink style={{ width: 15, height: 15 }} /></button>
                      <button onClick={() => openFiles(n)} className={iconBtn} title={t('sapnote.attachments')}><Paperclip style={{ width: 15, height: 15 }} /></button>
                      <button onClick={() => { setPendingPdf(null); setEditing({ ...n }); }} className={iconBtn} title={t('common.edit')}><Pencil style={{ width: 15, height: 15 }} /></button>
                      <button onClick={() => setConfirmDel(n)} className={iconBtn + ' hover:text-rose-600 hover:bg-rose-50'} title={t('common.delete')}><Trash2 style={{ width: 15, height: 15 }} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal tambah/edit */}
      {editing && (
        <div className="fixed inset-0 z-[950] bg-slate-900/40 flex items-center justify-center p-3" onClick={() => !busy && setEditing(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                <FileText style={{ width: 16, height: 16 }} className="text-brand-600" />
                {editing.id ? t('sapnote.editNote', { n: editing.note_number }) : t('sapnote.saveNewNote')}
              </h3>
              <button onClick={() => setEditing(null)} className="p-1.5 -mr-1.5 text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="p-5 space-y-3">
              {!editing.id && pendingPdf && (
                <div className="px-3 py-2 rounded-lg bg-brand-50 border border-brand-200 text-[12px] text-brand-700 flex items-center gap-1.5">
                  <FileText style={{ width: 13, height: 13 }} />{t('sapnote.pdfArchivedWith')}<span className="font-medium truncate">{pendingPdf.name}</span>
                </div>
              )}
              <Field label={t('sapnote.noteNumber')}>
                <input value={editing.note_number} inputMode="numeric"
                  onChange={(e) => setEditing({ ...editing, note_number: e.target.value.replace(/[^\d]/g, '') })}
                  className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-brand-400" />
              </Field>
              <Field label={t('sapnote.title')}>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  placeholder={t('sapnote.titlePlaceholder')}
                  className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-brand-400" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('sapnote.component')}><input value={editing.component} onChange={(e) => setEditing({ ...editing, component: e.target.value })} placeholder={t('sapnote.egComponent')} className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-brand-400" /></Field>
                <Field label={t('sapnote.system')}><input value={editing.system} onChange={(e) => setEditing({ ...editing, system: e.target.value })} placeholder={t('sapnote.egSystem')} className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-brand-400" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('sapnote.priority')}><input value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} placeholder={t('sapnote.egPriority')} className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-brand-400" /></Field>
                <Field label={t('sapnote.status')}>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                    className="w-full h-9 px-2.5 rounded-lg border border-slate-300 text-sm bg-white outline-none focus:border-brand-400">
                    <option value="review">{t('sapnote.statusReview')}</option>
                    <option value="applying">{t('sapnote.statusApplying')}</option>
                    <option value="done">{t('sapnote.statusDone')}</option>
                  </select>
                </Field>
              </div>
              <Field label={t('sapnote.remark')}>
                <textarea value={editing.remark} onChange={(e) => setEditing({ ...editing, remark: e.target.value })} rows={3}
                  placeholder={t('sapnote.remarkPlaceholder')}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-brand-400 resize-y" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
              <button onClick={() => setEditing(null)} disabled={busy} className="h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">{t('common.cancel')}</button>
              <button onClick={saveNote} disabled={busy} className="h-9 px-4 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700 flex items-center gap-1.5 disabled:opacity-60">
                {busy && <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />} {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal ekstensi capture SAP for Me */}
      {extModal && (
        <div className="fixed inset-0 z-[950] bg-slate-900/40 flex items-center justify-center p-3" onClick={() => setExtModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2"><DownloadCloud style={{ width: 16, height: 16 }} className="text-violet-600" />Capture extension (SAP for Me → Dashboard)</h3>
              <button onClick={() => setExtModal(null)} className="p-1.5 -mr-1.5 text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="p-5 space-y-3 text-[12.5px] text-slate-600">
              <p>Use this for <b>KBAs</b> (and any note) that SNOTE cannot download. Open the note on me.sap.com, click the extension icon, and the content is saved here as an archived, searchable attachment.</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Download <code className="px-1 bg-slate-100 rounded">/home/ec2-user/sapnote-extension.zip</code> via <b>File Deploy → File Explorer</b>, then extract it.</li>
                <li>In Chrome open <code className="px-1 bg-slate-100 rounded">chrome://extensions</code>, turn on <b>Developer mode</b>, click <b>Load unpacked</b> and choose the <b>sapnote-extension</b> folder.</li>
                <li>Pin the icon <b>“SAP Note to Dashboard”</b>. Click it once — the settings page opens. Paste the token below, click <b>Save</b>, then <b>Test connection</b>.</li>
                <li><b>One note:</b> open it on <b>me.sap.com/notes/&lt;number&gt;</b>, click the icon → <b>Capture this page</b>.</li>
                <li><b>Many KBAs:</b> just be logged in to me.sap.com, click the icon → <b>Capture queue</b>. The extension opens each queued KBA in a background tab and saves it here (about 10 s per note — keep Chrome open).</li>
              </ol>
              {extModal.queue != null && (
                <div className="px-3 py-2 rounded-lg bg-violet-50 border border-violet-200 text-violet-700">
                  Capture queue: <b>{extModal.queue}</b> note(s) in the list without content yet.
                </div>
              )}
              <p className="text-[11.5px] text-slate-400">Already installed v1.0? Download the zip again, replace the folder, then click the reload icon on the extension card in chrome://extensions.</p>
              <div>
                <div className="text-[12px] font-medium text-slate-700 mb-1">Capture token</div>
                {extModal.error ? <div className="text-rose-600">{extModal.error}</div> : (
                  <div className="flex gap-2">
                    <input readOnly value={extModal.token || 'Loading…'} onFocus={(e) => e.target.select()}
                      className="flex-1 h-9 px-3 rounded-lg border border-slate-300 text-[12px] font-mono bg-slate-50" />
                    <button disabled={!extModal.token}
                      onClick={() => { navigator.clipboard?.writeText(extModal.token); setExtModal((m) => ({ ...m, copied: true })); }}
                      className="h-9 px-3 rounded-lg bg-violet-600 text-white text-sm hover:bg-violet-700 disabled:opacity-50">{extModal.copied ? 'Copied' : 'Copy'}</button>
                  </div>
                )}
                <button onClick={rotateExtToken} className="mt-2 text-[11.5px] text-slate-500 hover:text-rose-600 hover:underline">Generate new token (old one stops working)</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal tambah banyak nomor */}
      {bulkOpen && (
        <div className="fixed inset-0 z-[950] bg-slate-900/40 flex items-center justify-center p-3" onClick={closeBulk}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                <ClipboardList style={{ width: 16, height: 16 }} className="text-brand-600" />Add multiple notes</h3>
              <button onClick={closeBulk} className="p-1.5 -mr-1.5 text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="p-5 space-y-3">
              {!bulkResult ? (<>
                <p className="text-[12.5px] text-slate-500">Paste note numbers separated by comma, space or new line. Title and component are filled from the system's SNOTE list when the note is already there.</p>
                <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={7} autoFocus
                  placeholder={'2554853\n2537133, 1872170'}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono outline-none focus:border-brand-400 resize-y" />
                <div className="flex items-center justify-between text-[12px] text-slate-500">
                  <span>{bulkNums.length} unique number(s) detected</span>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={bulkPull} onChange={(e) => setBulkPull(e.target.checked)} />
                    Pull &amp; archive content from system</label>
                </div>
              </>) : (
                <div className="space-y-2">
                  <div className="text-[13px] text-slate-700"><b>{bulkResult.created}</b> added · <b>{bulkResult.skipped}</b> already in list</div>
                  <div className="max-h-64 overflow-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-[12px]">
                      <tbody>
                        {(bulkResult.results || []).map((r) => (
                          <tr key={r.note_number} className="border-b border-slate-100">
                            <td className="px-3 py-1.5 font-medium">{r.note_number}</td>
                            <td className="px-3 py-1.5 text-slate-500">
                              {r.result === 'exists' ? 'Already in list' : r.in_system ? 'Added · found in SNOTE' : 'Added · not in SNOTE yet'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(bulkResult.results || []).some((r) => r.result === 'created' && !r.in_system) && (
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      <button disabled={sysDownloading}
                        onClick={async () => { const nums = bulkResult.results.filter((r) => r.result === 'created' && !r.in_system).map((r) => r.note_number); await downloadToSystem(nums); load(); }}
                        className="h-8 px-3 rounded-lg border border-brand-300 text-[12.5px] text-brand-700 bg-brand-50 hover:bg-brand-100 inline-flex items-center gap-1.5 disabled:opacity-60">
                        {sysDownloading ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <Server style={{ width: 13, height: 13 }} />}Download missing to system (SNOTE)</button>
                      <span className="text-[11px] text-slate-400">Requires the SNOTE download extension in the Z-service.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
              <button onClick={closeBulk} disabled={busy} className="h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">{bulkResult ? 'Close' : t('common.cancel')}</button>
              {!bulkResult && (
                <button onClick={runBulkAdd} disabled={busy || !bulkNums.length} className="h-9 px-4 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700 flex items-center gap-1.5 disabled:opacity-60">
                  {busy && <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />} Add {bulkNums.length || ''} note(s)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Konfirmasi hapus */}
      {confirmDel && (
        <div className="fixed inset-0 z-[950] bg-slate-900/40 flex items-center justify-center p-3" onClick={() => !busy && setConfirmDel(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-800 text-sm mb-1">{t('sapnote.deleteNoteTitle', { n: confirmDel.note_number })}</h3>
            <p className="text-[13px] text-slate-500 mb-4">{t('sapnote.deleteWarnOne')}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} disabled={busy} className="h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">{t('common.cancel')}</button>
              <button onClick={doDelete} disabled={busy} className="h-9 px-4 rounded-lg bg-rose-600 text-white text-sm hover:bg-rose-700 flex items-center gap-1.5 disabled:opacity-60">
                {busy && <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />} {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Konfirmasi hapus massal */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-[950] bg-slate-900/40 flex items-center justify-center p-3" onClick={() => !busy && setBulkConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-800 text-sm mb-1">Hapus {selected.size} note terpilih?</h3>
            <p className="text-[13px] text-slate-500 mb-4">{t('sapnote.deleteWarnMany')}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setBulkConfirm(false)} disabled={busy} className="h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">{t('common.cancel')}</button>
              <button onClick={bulkDelete} disabled={busy} className="h-9 px-4 rounded-lg bg-rose-600 text-white text-sm hover:bg-rose-700 flex items-center gap-1.5 disabled:opacity-60">
                {busy && <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />} {t('common.delete')} {selected.size}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manajer lampiran (banyak file per note) */}
      {filesModal && (
        <div className="fixed inset-0 z-[950] bg-slate-900/40 flex items-center justify-center p-3" onClick={() => setFilesModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                <Paperclip style={{ width: 16, height: 16 }} className="text-brand-600" /> {t('sapnote.attachmentsTitle', { n: filesModal.note.note_number })}
              </h3>
              <button onClick={() => setFilesModal(null)} className="p-1.5 -mr-1.5 text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="p-5">
              <label className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm cursor-pointer mb-3 ${busy ? 'bg-slate-100 text-slate-400' : 'bg-brand-600 text-white hover:bg-brand-700'}`}>
                {busy ? <Loader2 className="animate-spin" style={{ width: 15, height: 15 }} /> : <Upload style={{ width: 15, height: 15 }} />} {t('sapnote.addAttachment')}
                <input type="file" multiple className="hidden" disabled={busy}
                  onChange={(e) => { uploadFiles(filesModal.note, e.target.files); e.target.value = ''; }} />
              </label>
              <p className="text-[11px] text-slate-400 mb-3">{t('sapnote.uploadHint')}</p>

              {filesModal.files === null ? (
                <div className="py-8 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" style={{ width: 16, height: 16 }} />{t('sapnote.loading')}</div>
              ) : filesModal.files.length === 0 ? (
                <div className="py-8 text-center text-slate-400 text-sm">{t('sapnote.noAttachments')}</div>
              ) : (
                <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
                  {filesModal.files.map((f) => (
                    <div key={f.id} className="flex items-center gap-2 px-3 py-2.5">
                      <FileText style={{ width: 15, height: 15 }} className="text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] text-slate-800 truncate" title={f.name}>{f.name}</div>
                        <div className="text-[11px] text-slate-400">{fmtBytes(f.size)} · {f.uploaded_by || '—'} · {fmtTs(f.uploaded_at)}</div>
                      </div>
                      {isPreviewable(f.name) && (
                        <button onClick={() => viewFile(filesModal.note, f)} className={iconBtn} title={t('sapnote.view')}><Eye style={{ width: 15, height: 15 }} /></button>
                      )}
                      <button onClick={() => downloadFile(filesModal.note, f)} className={iconBtn} title={t('sapnote.download')}><Download style={{ width: 15, height: 15 }} /></button>
                      <button onClick={() => deleteFile(filesModal.note, f)} disabled={busy} className={iconBtn + ' hover:text-rose-600 hover:bg-rose-50'} title={t('common.delete')}><Trash2 style={{ width: 15, height: 15 }} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview satu file (PDF/gambar/teks) */}
      {pdfViewer && (
        <div className="fixed inset-0 z-[955] bg-slate-900/50 flex items-center justify-center p-3" onClick={() => { URL.revokeObjectURL(pdfViewer.url); setPdfViewer(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800 text-sm truncate">{pdfViewer.name || `Note ${pdfViewer.note_number}`}</h3>
              <div className="flex items-center gap-1.5">
                <a href={pdfViewer.url} download={pdfViewer.name || `note_${pdfViewer.note_number}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title={t('sapnote.download')}><Download style={{ width: 16, height: 16 }} /></a>
                <button onClick={() => { URL.revokeObjectURL(pdfViewer.url); setPdfViewer(null); }} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
              </div>
            </div>
            <iframe title={t('sapnote.preview')} src={pdfViewer.url} className="flex-1 w-full" />
          </div>
        </div>
      )}

      {/* Viewer note SERAGAM — HTML (dari sistem) / PDF (upload) via ikon mata */}
      {noteView && (
        <div className="fixed inset-0 z-[956] bg-slate-900/50 flex items-center justify-center p-3" onClick={closeNoteView}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800 text-sm truncate flex items-center gap-2">
                <FileText style={{ width: 16, height: 16 }} className="text-brand-600 shrink-0" />
                {t('sapnote.noteContentTitle', { n: noteView.note.note_number })}{noteView.name ? ` · ${noteView.name}` : ''}
              </h3>
              <button onClick={closeNoteView} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-50">
              {noteView.status === 'loading' ? (
                <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" style={{ width: 18, height: 18 }} />{t('sapnote.loading')}</div>
              ) : noteView.status === 'working' ? (
                <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" style={{ width: 18, height: 18 }} />{t('sapnote.pullingArchiving')}</div>
              ) : noteView.status === 'html' ? (
                <iframe title={t('sapnote.noteContent')} sandbox="" srcDoc={noteView.html} className="w-full h-full bg-white" />
              ) : noteView.status === 'pdf' ? (
                <iframe title={t('sapnote.noteContent')} src={noteView.url} className="w-full h-full" />
              ) : noteView.status === 'kba' ? (
                <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-6">
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-50 text-violet-700 border border-violet-200">KBA</span>
                  <p className="text-[13px] text-slate-600 max-w-md">
                    {noteView.note.note_number} is a <b>Knowledge Base Article</b>. KBAs exist only in SAP for Me and cannot be downloaded by SNOTE.
                    Open it in the portal and click the <b>Capture extension</b> icon — or save the <b>PDF</b> and upload it here.</p>
                  <div className="flex items-center gap-2 flex-wrap justify-center">
                    <button onClick={() => openInPortal(noteView.note.note_number)}
                      className="h-9 px-3.5 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
                      <ExternalLink style={{ width: 15, height: 15 }} />Open in SAP for Me</button>
                    <label className="h-9 px-3.5 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700 inline-flex items-center gap-1.5 cursor-pointer">
                      <Upload style={{ width: 15, height: 15 }} />Upload PDF
                      <input type="file" accept="application/pdf,.pdf,.html,.htm" className="hidden"
                        onChange={async (e) => { const fl = e.target.files; const nt = noteView.note; e.target.value = ''; await uploadFiles(nt, fl); openNoteView(nt); }} />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-6">
                  <p className="text-[13px] text-slate-500 max-w-md">{t('sapnote.noContentSaved')}</p>
                  <button onClick={() => fetchAndArchiveNote(noteView.note)}
                    className="h-9 px-3.5 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700 inline-flex items-center gap-1.5">
                    <DownloadCloud style={{ width: 15, height: 15 }} />{t('sapnote.loadArchive')}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tarik dari sistem — pilih note untuk diimpor */}
      {sysModal && (
        <div className="fixed inset-0 z-[950] bg-slate-900/40 flex items-center justify-center p-3" onClick={() => !importing && setSysModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                <Server style={{ width: 16, height: 16 }} className="text-brand-600" />
                Note di sistem {sysModal.system && <span className="text-brand-600">{sysModal.system}</span>}
              </h3>
              <button onClick={() => setSysModal(null)} className="p-1.5 -mr-1.5 text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
            </div>

            {sysModal.loading ? (
              <div className="py-16 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" style={{ width: 18, height: 18 }} />{t('sapnote.contactingSap')}</div>
            ) : sysModal.notes.length === 0 ? (
              <div className="py-16 text-center text-slate-400 text-sm">{t('sapnote.noNotesInSystem')}</div>
            ) : (
              <>
                {/* Filter: cari + status */}
                <div className="px-5 py-2.5 flex items-center gap-2 border-b border-slate-100 flex-wrap">
                  <div className="relative flex-1 min-w-[160px]">
                    <Search style={{ width: 14, height: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={sysSearch} onChange={(e) => setSysSearch(e.target.value)} placeholder={t('sapnote.searchSysPlaceholder')}
                      className="w-full h-8 pl-8 pr-3 rounded-lg border border-slate-300 text-[13px] outline-none focus:border-brand-400" />
                  </div>
                  <select value={sysStatusF} onChange={(e) => setSysStatusF(e.target.value)}
                    className="h-8 px-2 rounded-lg border border-slate-300 text-[13px] bg-white outline-none">
                    <option value="">{t('sapnote.allStatus')}</option>
                    {sysStatusOpts.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="px-5 py-2 flex items-center gap-3 border-b border-slate-100 text-[12px] text-slate-500">
                  <button onClick={() => setSysModal((m) => ({ ...m, sel: new Set(sysVisible.filter((n) => !n.in_register).map((n) => n.note_number)) }))} className="text-brand-600 hover:underline">{t('sapnote.selectNew')}</button>
                  <button onClick={() => setSysModal((m) => { const sel = new Set(m.sel); sysVisible.forEach((n) => sel.add(n.note_number)); return { ...m, sel }; })} className="text-brand-600 hover:underline" title={t('sapnote.selectAllShownTitle')}>{t('sapnote.selectAllShown')}</button>
                  <button onClick={() => setSysModal((m) => ({ ...m, sel: new Set() }))} className="text-slate-500 hover:underline">{t('sapnote.clear')}</button>
                  <span className="ml-auto">{sysModal.sel.size} dipilih · {sysVisible.length} tampil dari {sysModal.notes.length}</span>
                </div>
                <div className="overflow-auto flex-1">
                  <table className="w-full text-[12.5px]" style={{ borderCollapse: 'collapse' }}>
                    <thead className="sticky top-0">
                      <tr className="bg-slate-50 text-slate-500">
                        <th className="px-3 py-2 w-8 border-b border-slate-200"></th>
                        {[['note_number', t('sapnote.colNumber')], ['version', t('sapnote.version')], ['component', t('sapnote.component')], ['impl_label', t('sapnote.statusInSystem')]].map(([k, label]) => (
                          <th key={k} onClick={() => sysToggleSort(k)}
                            className="px-3 py-2 text-left font-medium text-[11px] uppercase tracking-wide border-b border-slate-200 cursor-pointer select-none hover:text-slate-700">
                            {label}{sysSortArrow(k)}
                          </th>
                        ))}
                        <th className="px-3 py-2 border-b border-slate-200"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sysVisible.length === 0 ? (
                        <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">{t('sapnote.noMatch')}</td></tr>
                      ) : sysVisible.map((n) => (
                        <tr key={n.note_number} className={`hover:bg-slate-50/60 ${n.in_register ? 'opacity-60' : ''}`}>
                          <td className="px-3 py-2 border-b border-slate-100">
                            <input type="checkbox" checked={sysModal.sel.has(n.note_number)} onChange={() => toggleSel(n.note_number)} />
                          </td>
                          <td className="px-3 py-2 border-b border-slate-100 text-brand-600 font-medium">{n.note_number}</td>
                          <td className="px-3 py-2 border-b border-slate-100 text-slate-500">{n.version || '—'}</td>
                          <td className="px-3 py-2 border-b border-slate-100 text-slate-600">{n.component || '—'}</td>
                          <td className="px-3 py-2 border-b border-slate-100 text-slate-600">{n.impl_label || '—'}</td>
                          <td className="px-3 py-2 border-b border-slate-100">
                            {n.in_register && <span className="text-[11px] text-slate-400">{t('sapnote.exists')}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-between items-center gap-3 px-5 py-3 border-t border-slate-100 flex-wrap">
                  <label className="flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer" title={t('sapnote.skipHint')}>
                    <input type="checkbox" checked={autoArchive} onChange={(e) => setAutoArchive(e.target.checked)} disabled={importing} />{t('sapnote.alsoArchive')}</label>
                  <div className="flex gap-2">
                    <button onClick={() => setSysModal(null)} disabled={importing} className="h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">{t('common.cancel')}</button>
                    <button onClick={importSelected} disabled={importing || sysModal.sel.size === 0}
                      className="h-9 px-4 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700 flex items-center gap-1.5 disabled:opacity-50">
                      {importing && <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />} Impor {sysModal.sel.size ? `(${sysModal.sel.size})` : ''}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Detail note (Properties + Status, gaya SNOTE) */}
      {detail && (() => {
        const n = detail.note;
        const meta = parseRemarkMeta(n.remark);
        const curIdx = STATUS_STEPS.indexOf(n.status);
        const rows = [
          [t('sapnote.noteNumber'), n.note_number, FileText],
          [t('sapnote.title'), n.title || '—', ClipboardList],
          [t('sapnote.component'), n.component || '—', Layers],
          [t('sapnote.system'), n.system || meta.fromSystem || '—', Server],
          [t('sapnote.priority'), n.priority || '—', AlertTriangle],
          [t('sapnote.version'), meta.version || '—', Info],
          [t('sapnote.processedBy'), meta.processedBy || '—', User],
        ];
        return (
          <div className="fixed inset-0 z-[952] bg-slate-900/40 flex items-center justify-center p-3" onClick={() => setDetail(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-start justify-between gap-3 px-5 py-3.5 border-b border-slate-100">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <FileText style={{ width: 16, height: 16 }} className="text-brand-600 shrink-0" />
                    <button onClick={() => openInPortal(n.note_number)} className="font-bold text-slate-800 text-[15px] hover:text-brand-600 hover:underline" title={t('sapnote.openPortal')}>
                      SAP Note {n.note_number}
                    </button>
                    <StatusBadge status={n.status} />
                  </div>
                  <div className="text-[13px] text-slate-500 truncate pl-6" title={n.title}>{n.title || 'Tanpa judul'}</div>
                </div>
                <button onClick={() => setDetail(null)} className="p-1.5 -mr-1.5 text-slate-400 hover:text-slate-600 shrink-0"><X style={{ width: 18, height: 18 }} /></button>
              </div>

              <div className="overflow-auto p-5 space-y-4">
                {/* Status stepper */}
                <div className="border border-slate-200 rounded-xl p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">{t('sapnote.applyStatus')}</div>
                  <div className="flex items-center">
                    {STATUS_STEPS.map((st, i) => {
                      const done = i < curIdx, active = i === curIdx;
                      return (
                        <div key={st} className="flex items-center flex-1 last:flex-none">
                          <button onClick={() => quickStatus(n, st)} disabled={busy}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium transition disabled:opacity-60
                              ${active ? (STATUS[st]?.cls || 'bg-brand-50 text-brand-700 border-brand-200') + ' border'
                                       : done ? 'text-emerald-600 hover:bg-emerald-50'
                                              : 'text-slate-400 hover:bg-slate-100'}`}
                            title={t('sapnote.changeStatusTo', { status: STATUS[st] ? t(STATUS[st].key) : st })}>
                            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] border
                              ${active ? 'border-current' : done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300'}`}>
                              {done ? <Check style={{ width: 10, height: 10 }} /> : i + 1}
                            </span>
                            {STATUS[st] ? t(STATUS[st].key) : st}
                          </button>
                          {i < STATUS_STEPS.length - 1 && (
                            <div className={`flex-1 h-px mx-1.5 ${i < curIdx ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2.5">{t('sapnote.stepperHint')}</p>
                </div>

                {/* Properties */}
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{t('sapnote.properties')}</div>
                  <div className="divide-y divide-slate-100">
                    {rows.map(([label, value, Icon]) => (
                      <div key={label} className="flex items-start gap-3 px-4 py-2.5">
                        <div className="flex items-center gap-1.5 w-32 shrink-0 text-[12px] text-slate-500">
                          <Icon style={{ width: 13, height: 13 }} className="text-slate-400 shrink-0" /> {label}
                        </div>
                        <div className="text-[13px] text-slate-800 break-words min-w-0">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Isi note (long text HTML) dari sistem — lazy, dirender read-only */}
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex items-center justify-between gap-2">
                    <span>{t('sapnote.noteContentFromSystem')}</span>
                    {sysContent && (sysContent.has_text || sysContent.error) && (
                      <button onClick={() => loadAndArchiveDetail(n)} disabled={sysContent.loading}
                        className="text-brand-600 hover:underline normal-case tracking-normal font-medium flex items-center gap-1 disabled:opacity-50">
                        <DownloadCloud style={{ width: 12, height: 12 }} />{t('sapnote.reload')}</button>
                    )}
                  </div>
                  <div className="p-4">
                    {sysContent === null ? (
                      <div className="text-center py-2">
                        <button onClick={() => loadAndArchiveDetail(n)}
                          className="h-9 px-3.5 rounded-lg border border-brand-300 text-sm text-brand-700 bg-brand-50 hover:bg-brand-100 inline-flex items-center gap-1.5">
                          <DownloadCloud style={{ width: 15, height: 15 }} />{t('sapnote.loadContent')}</button>
                        <p className="text-[11px] text-slate-400 mt-2">{t('sapnote.pullSrsHint')}</p>
                      </div>
                    ) : sysContent.loading ? (
                      <div className="py-8 text-center text-slate-400 text-[13px]"><Loader2 className="inline animate-spin mr-2" style={{ width: 16, height: 16 }} />{t('sapnote.contactingSap')}</div>
                    ) : sysContent.notConfigured ? (
                      <div className="px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[12.5px] text-amber-700 flex items-start gap-1.5">
                        <AlertTriangle style={{ width: 14, height: 14 }} className="mt-0.5 shrink-0" />{t('sapnote.pullNotConfigured')}</div>
                    ) : sysContent.error ? (
                      <div className="px-3 py-2.5 rounded-lg bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700">{sysContent.error}</div>
                    ) : (sysContent.kba || (sysContent.needs_download && n.note_type === 'KBA')) ? (
                      <div className="space-y-2.5">
                        <div className="px-3 py-2.5 rounded-lg bg-violet-50 border border-violet-200 text-[12.5px] text-violet-700 flex items-start gap-1.5">
                          <Info style={{ width: 14, height: 14 }} className="mt-0.5 shrink-0" />
                          <span>This is a <b>KBA</b> (Knowledge Base Article). SNOTE cannot download KBAs — open it in SAP for Me, click <b>PDF</b>, and upload it via Attachments.</span>
                        </div>
                        <button onClick={() => openInPortal(n.note_number)}
                          className="h-8 px-3 rounded-lg border border-slate-300 text-[12.5px] text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
                          <ExternalLink style={{ width: 13, height: 13 }} />Open in SAP for Me</button>
                      </div>
                    ) : sysContent.needs_download ? (
                      <div className="space-y-2.5">
                        <div className="px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[12.5px] text-amber-700 flex items-start gap-1.5">
                          <AlertTriangle style={{ width: 14, height: 14 }} className="mt-0.5 shrink-0" />
                          <span>{t('sapnote.notDownloaded')}<strong>{t('sapnote.snote')}</strong> (Note Assistant) in the SAP system, then click "Reload".</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button onClick={() => downloadToSystem([n.note_number], n)} disabled={sysDownloading}
                            className="h-8 px-3 rounded-lg border border-brand-300 text-[12.5px] text-brand-700 bg-brand-50 hover:bg-brand-100 inline-flex items-center gap-1.5 disabled:opacity-60">
                            {sysDownloading ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <Server style={{ width: 13, height: 13 }} />}Download to system (SNOTE)</button>
                          <button onClick={() => openInPortal(n.note_number)}
                            className="h-8 px-3 rounded-lg border border-slate-300 text-[12.5px] text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
                            <ExternalLink style={{ width: 13, height: 13 }} />{t('sapnote.openPortal')}</button>
                          <button onClick={() => loadAndArchiveDetail(n)}
                            className="h-8 px-3 rounded-lg border border-slate-300 text-[12.5px] text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
                            <DownloadCloud style={{ width: 13, height: 13 }} />{t('sapnote.reload')}</button>
                        </div>
                        <p className="text-[11px] text-slate-400">{t('sapnote.snoteStep')}<b>{t('sapnote.snote')}</b> → SAP Note Browser → enter number {n.note_number} → Download.</p>
                      </div>
                    ) : !sysContent.has_text ? (
                      <div className="px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200 text-[12.5px] text-slate-500">{t('sapnote.htmlUnavailable')}</div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                          <div className="text-[11px] text-slate-400">
                            Versi {sysContent.version || '—'}{sysContent.language ? ` · bahasa ${sysContent.language}` : ''} · {sysContent.fromArchive ? 'dari arsip' : 'ditarik dari sistem'} (read-only)
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => loadAndArchiveDetail(n)} disabled={sysContent.loading}
                              className="h-8 px-2.5 rounded-lg border border-slate-300 text-[12.5px] text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50"
                              title={t('sapnote.repullTitle')}>
                              <DownloadCloud style={{ width: 13, height: 13 }} />{t('sapnote.reloadFromSystem')}</button>
                            <button onClick={() => downloadSystemHtml(n, sysContent.html, sysContent.version)}
                              className="h-8 px-2.5 rounded-lg border border-slate-300 text-[12.5px] text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
                              title={t('sapnote.downloadHtmlTitle')}>
                              <Download style={{ width: 13, height: 13 }} />{t('sapnote.download')}</button>
                          </div>
                        </div>
                        {/* Sandbox kosong: tanpa script & tanpa same-origin → HTML asing tak bisa menyentuh app. */}
                        <iframe title={t('sapnote.noteContentSystem')} sandbox="" srcDoc={sysContent.html}
                          className="w-full rounded-lg border border-slate-200 bg-white" style={{ height: 460 }} />
                      </>
                    )}
                  </div>
                </div>

                {/* Catatan */}
                {n.remark ? (
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{t('sapnote.remark')}</div>
                    <div className="px-4 py-3 text-[13px] text-slate-700 whitespace-pre-wrap break-words">{n.remark}</div>
                  </div>
                ) : null}

                {/* Lampiran */}
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex items-center justify-between">
                    <span>Lampiran{detail.files ? ` (${detail.files.length})` : ''}</span>
                    <button onClick={() => { const note = n; setDetail(null); openFiles(note); }} className="text-brand-600 hover:underline normal-case tracking-normal font-medium flex items-center gap-1">
                      <Paperclip style={{ width: 12, height: 12 }} />{t('sapnote.manage')}</button>
                  </div>
                  {detail.files === null ? (
                    <div className="py-6 text-center text-slate-400 text-[13px]"><Loader2 className="inline animate-spin mr-2" style={{ width: 15, height: 15 }} />{t('sapnote.loading')}</div>
                  ) : detail.files.length === 0 ? (
                    <div className="py-6 text-center text-slate-400 text-[13px]">{t('sapnote.noAttachments')}</div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {detail.files.map((f) => (
                        <div key={f.id} className="flex items-center gap-2 px-4 py-2.5">
                          <FileText style={{ width: 15, height: 15 }} className="text-slate-400 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] text-slate-800 truncate" title={f.name}>{f.name}</div>
                            <div className="text-[11px] text-slate-400">{fmtBytes(f.size)} · {f.uploaded_by || '—'} · {fmtTs(f.uploaded_at)}</div>
                          </div>
                          {isPreviewable(f.name) && (
                            <button onClick={() => viewFile(n, f)} className={iconBtn} title={t('sapnote.view')}><Eye style={{ width: 15, height: 15 }} /></button>
                          )}
                          <button onClick={() => downloadFile(n, f)} className={iconBtn} title={t('sapnote.download')}><Download style={{ width: 15, height: 15 }} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Audit */}
                <div className="flex items-center gap-4 text-[11px] text-slate-400 flex-wrap">
                  <span className="flex items-center gap-1"><Clock style={{ width: 12, height: 12 }} /> Dibuat {fmtTs(n.created_at)}{n.created_by ? ` oleh ${n.created_by}` : ''}</span>
                  <span className="flex items-center gap-1"><Clock style={{ width: 12, height: 12 }} /> Diubah {fmtTs(n.updated_at)}{n.updated_by ? ` oleh ${n.updated_by}` : ''}</span>
                </div>
              </div>

              {/* Footer aksi */}
              <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
                <button onClick={() => openInPortal(n.note_number)} className="h-9 px-3.5 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-1.5">
                  <ExternalLink style={{ width: 15, height: 15 }} />{t('sapnote.openPortal')}</button>
                <button onClick={() => { const note = n; setDetail(null); setPendingPdf(null); setEditing({ ...note }); }} className="h-9 px-3.5 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700 flex items-center gap-1.5">
                  <Pencil style={{ width: 15, height: 15 }} />{t('common.edit')}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Progres arsip otomatis saat impor */}
      {archiveJob && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[960] px-4 py-2.5 rounded-xl shadow-lg text-sm text-white bg-brand-600 flex items-center gap-2">
          <Loader2 className="animate-spin" style={{ width: 15, height: 15 }} />
          Mengarsipkan isi note… {archiveJob.done}/{archiveJob.total}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-5 left-1/2 -translate-x-1/2 z-[960] px-4 py-2.5 rounded-xl shadow-lg text-sm text-white ${toast.type === 'error' ? 'bg-rose-600' : 'bg-emerald-600'}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
